import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { ProjectImages, ProjectImage } from './types';

// The images service answers failures as { success: false } instead of throwing
function ensure<T extends { success: boolean; error?: string }>(result: T, what: string): T {
  if (!result.success) throw new Error(what, { cause: result.error });
  return result;
}

export class ProjectImagesService {
  constructor(private services: ReturnType<typeof useServices>) {}

  /**
   * Fetch all project images and convert to structured format
   */
  async fetchProjectImages(projectInstanceId: string): Promise<ProjectImages> {
    try {
      // The portrait and the landscape images in one read, newest first, as each category's own read gave them
      const imagesResponse = await this.services.uiImages.getUIImages({
        projectInstanceId: projectInstanceId,
        imageCategories: ['ProjectPortrait', 'ProjectLandscape']
      });
      // A failed read is not "no images": an upload over it would add a second portrait
      ensure(imagesResponse, "We couldn't load the project images.");
      const ofCategory = (category: string) => ({ ...imagesResponse, data: imagesResponse.data?.filter(image => image.imageCategory === category) });
      const portraitImagesResponse = ofCategory('ProjectPortrait');
      const landscapeImagesResponse = ofCategory('ProjectLandscape');
      
      // Convert to structured format
      return {
        portrait: portraitImagesResponse.success && portraitImagesResponse.data && portraitImagesResponse.data.length > 0 ? {
          id: portraitImagesResponse.data[0].imageId,
          url: portraitImagesResponse.data[0].imageCroppedUrl || portraitImagesResponse.data[0].imageOriginalUrl || '',
          originalUrl: portraitImagesResponse.data[0].imageOriginalUrl,
          aspectRatio: '9:16' as const,
          orderNumber: 1
        } : null,
        landscape: landscapeImagesResponse.success && landscapeImagesResponse.data ? 
          landscapeImagesResponse.data
            .sort((a, b) => (a.imageOrderNumber || 0) - (b.imageOrderNumber || 0))
            .map((img) => ({
              id: img.imageId,
              url: img.imageCroppedUrl || img.imageOriginalUrl || '',
              originalUrl: img.imageOriginalUrl,
              aspectRatio: '16:9' as const,
              orderNumber: img.imageOrderNumber || 1
            })) : []
      };
    } catch (error) {
      console.error('Error fetching project images:', error);
      throw new Error('Failed to fetch project images', { cause: error });
    }
  }

  /**
   * Create a new project image
   */
  async createProjectImage(
    projectInstanceId: string,
    userId: string,
    imageCategory: 'ProjectPortrait' | 'ProjectLandscape',
    imageOriginalUrl: string,
    imageCroppedUrl?: string,
    imageMetadata?: { crop?: { x: number; y: number; width: number; height: number } }
  ): Promise<void> {
    try {
      let imageOrderNumber = 1;
      
      if (imageCategory === 'ProjectLandscape') {
        // For landscape images, find the next available order number
        const existingLandscapeImages = ensure(await this.services.uiImages.getUIImages({
          projectInstanceId: projectInstanceId,
          imageCategory: 'ProjectLandscape'
        }), "We couldn't read the existing images, so the image wasn't added.");
        
        if (existingLandscapeImages.data && existingLandscapeImages.data.length > 0) {
          const maxOrder = Math.max(...existingLandscapeImages.data.map(img => img.imageOrderNumber || 0));
          imageOrderNumber = maxOrder + 1;
        }
      }
      
      const createParams = {
        projectInstanceId: projectInstanceId,
        imageCategory: imageCategory,
        imageOriginalUrl: imageOriginalUrl,
        imageCroppedUrl: imageCroppedUrl,
        userId: userId,
        imageOrderNumber,
        imageMetadata
      };
      
      ensure(await this.services.uiImages.createUIImage(createParams), 'Failed to create project image');
    } catch (error) {
      console.error('Error creating project image:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to create project image', { cause: error });
    }
  }

  /**
   * Update an existing project image
   */
  async updateProjectImage(
    imageId: string, 
    imageOriginalUrl: string,
    imageCroppedUrl?: string,
    imageMetadata?: { crop?: { x: number; y: number; width: number; height: number } }
  ): Promise<void> {
    try {
      ensure(await this.services.uiImages.updateUIImage(imageId, {
        imageOriginalUrl: imageOriginalUrl,
        imageCroppedUrl: imageCroppedUrl,
        ...(imageMetadata && { imageMetadata })
      }), 'Failed to update project image');
    } catch (error) {
      console.error('Error updating project image:', error);
      throw new Error('Failed to update project image', { cause: error });
    }
  }

  /**
   * Delete a project image
   */
  async deleteProjectImage(imageId: string): Promise<void> {
    try {
      ensure(await this.services.uiImages.deleteUIImage(imageId), 'Failed to delete project image');
    } catch (error) {
      console.error('Error deleting project image:', error);
      throw new Error('Failed to delete project image', { cause: error });
    }
  }

  /**
   * Reorder landscape images and update database
   */
  async reorderLandscapeImages(projectInstanceId: string, reorderedImages: ProjectImage[]): Promise<void> {
    try {
      // Update each image's order number in the database
      for (const img of reorderedImages) {
        ensure(await this.services.uiImages.updateUIImage(img.id, {
          imageOrderNumber: img.orderNumber
        }), 'Failed to update image order');
      }
    } catch (error) {
      console.error('Error updating image order numbers:', error);
      throw new Error('Failed to update image order', { cause: error });
    }
  }

  /**
   * Reorder remaining images after deletion
   */
  async reorderAfterDeletion(projectInstanceId: string): Promise<void> {
    try {
      const remainingImages = ensure(await this.services.uiImages.getUIImages({
        projectInstanceId: projectInstanceId,
        imageCategory: 'ProjectLandscape'
      }), 'Failed to update order numbers after deletion');
      
      if (remainingImages.data) {
        // Update each remaining image's order number in the database
        for (let i = 0; i < remainingImages.data.length; i++) {
          const img = remainingImages.data[i];
          ensure(await this.services.uiImages.updateUIImage(img.imageId, {
            imageOrderNumber: i + 1
          }), 'Failed to update order numbers after deletion');
        }
      }
    } catch (error) {
      console.error('Error updating order numbers after deletion:', error);
      throw new Error('Failed to update order numbers after deletion', { cause: error });
    }
  }
}
